import type { Issue } from '../../store/api/githubApi';
import { SEVERITY_ORDER } from './constants';

export function worstSeverity(issues: Issue[]): string {
  for (const sev of SEVERITY_ORDER) {
    if (issues.some((i) => i.severity === sev)) return sev;
  }
  return 'info';
}

export function scoreColor(score: number | null): 'success' | 'warning' | 'error' {
  if (score === null) return 'error';
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
}

export function scoreGrade(score: number | null): string {
  if (score === null) return '—';
  if (score >= 90) return 'A';
  if (score >= 75) return 'B';
  if (score >= 60) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

// USD — senior engineer blended rate, the single rate used everywhere on the
// scan results page. Mirrors debtiq-backend's app.core.remediation_cost.
// HOURLY_RATE_USD exactly — kept here too (rather than only reading it off
// the API response) because SavingsTab's per-issue displays need it without
// a scan-level fetch.
export const HOURLY_RATE = 150;

// Per-issue hours for display purposes (e.g. "~3.2 hrs" next to one issue in
// SavingsTab) — derived from the backend's deterministic, per-rule
// remediation_minutes (app/core/remediation_cost.py), never from
// ai_estimated_fix_time. That field is free LLM text, display-only, and is
// never parsed into a number anywhere anymore: a single malformed value
// ("Covered by 58606 fix", read as 58,606 hours) computed a $255.8M
// remediation figure for a ~5,000 line repo on 2026-10-21. See that incident
// and app/core/remediation_cost.py's module docstring for the full story —
// remediation cost now depends only on which rule fired, never on LLM free
// text, file size, or severity bucket.
export function issueFixHours(issue: Issue): number {
  return (issue.remediation_minutes ?? 0) / 60;
}

const _TEST_PATH_RE = /(?:^|\/)(tests?|spec|__tests?__|conftest|test_[^/]+|[^/]+_test)(?:\/|$)/i;

const _isTestFile = (filePath: string) => _TEST_PATH_RE.test(filePath);

export function calcBusinessImpact(issues: Issue[]) {
  // Sum of the backend's deterministic per-issue remediation_minutes — see
  // issueFixHours above for why this is never parsed from free text.
  const remediationCost = issues.reduce(
    (sum, i) => sum + issueFixHours(i) * HOURLY_RATE, 0
  );
  // CVEs from Trivy/pip-audit/npm-audit are stored as category="dependencies".
  // Code-level security issues from bandit/PMD are category="security".
  // Both represent security exposure — count both.
  // Exclude test files: assert statements and fixture credentials in tests are
  // expected patterns, not production security risks.
  // Sum the backend's real per-issue estimate (EPSS/breach-cost-based, see
  // app/core/security_risk.py) — never a separate hardcoded table here, or this
  // card silently drifts from what each issue's own detail view shows.
  const securityExposure = issues
    .filter((i) => (i.category === 'security' || i.category === 'dependencies') && !_isTestFile(i.file_path))
    .reduce((sum, i) => sum + (i.security_risk_usd ?? 0), 0);
  const fixableIssues = issues.filter(
    (i) => ['trivial', 'simple', 'moderate'].includes(i.ai_fix_complexity ?? '') && i.status !== 'pr_raised'
  );
  const automatedSavings = fixableIssues.reduce(
    (sum, i) => sum + issueFixHours(i) * HOURLY_RATE, 0
  );
  return { remediationCost, securityExposure, automatedSavings, fixableCount: fixableIssues.length };
}

// SQALE Technical Debt Ratio letter grade — SonarQube's published default
// maintainability-rating thresholds (≤5% A, ≤10% B, ≤20% C, ≤50% D, else E),
// adopted as-is to stay traceable to the same named convention
// SQALE_DEV_MINUTES_PER_LOC already cites, not an invented scale. Inverse of
// scoreGrade/scoreColor: here LOWER is better, since this is a cost ratio,
// not a 0-100 health score.
export function tdrGrade(pct: number | null): string {
  if (pct === null) return '—';
  if (pct <= 5) return 'A';
  if (pct <= 10) return 'B';
  if (pct <= 20) return 'C';
  if (pct <= 50) return 'D';
  return 'E';
}

export function tdrColor(pct: number | null): 'success' | 'warning' | 'error' {
  if (pct === null) return 'error';
  if (pct <= 10) return 'success';
  if (pct <= 20) return 'warning';
  return 'error';
}

export function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}
